// ChangeWithdrawalContext.tsx
import React, {
    createContext, useContext, useReducer,
} from 'react';
import {
    ACTIVE_SWITCHER_ADJUST, ACTIVE_SWITCHER_STOP, ADJUST_WITHDRAWAL, STOP_WITHDRAWAL,
} from './constants';
import {
    ChangeWithdrawalAction, ChangeWithdrawalContextType, ChangeWithdrawalProviderProps, ChangeWithdrawalState,
} from './ChangeWithdrawalManager.types';

// Define the initial state
const initialState: ChangeWithdrawalState = {
    activeSwitcher: ACTIVE_SWITCHER_ADJUST,
    inProgress: false,
};

// Define reducer function
function changeWithdrawalReducer(state: ChangeWithdrawalState, action: ChangeWithdrawalAction): ChangeWithdrawalState {
    switch (action.type) {
        case ADJUST_WITHDRAWAL:
            return { ...state, activeSwitcher: ACTIVE_SWITCHER_ADJUST };
        case STOP_WITHDRAWAL:
            return { ...state, activeSwitcher: ACTIVE_SWITCHER_STOP };
        default:
            throw new Error(`Unknown action type: ${action}`);
    }
}

// Create context
const ChangeWithdrawalContext = createContext<ChangeWithdrawalContextType | undefined>(undefined);

export const ChangeWithdrawalProvider = ({ children }: ChangeWithdrawalProviderProps) => {
    const [state, dispatch] = useReducer(changeWithdrawalReducer, initialState);

    const handleAdjustWithdrawal = () => {
        dispatch({ type: ADJUST_WITHDRAWAL });
    };

    const handleStopWithdrawal = () => {
        dispatch({ type: STOP_WITHDRAWAL });
    };

    return (
        <ChangeWithdrawalContext.Provider value={{ state, dispatch, actions: { handleAdjustWithdrawal, handleStopWithdrawal } }}>
            {children}
        </ChangeWithdrawalContext.Provider>
    );
};

// Custom hook for using ChangeWithdrawal context
export function useChangeWithdrawal() {
    const context = useContext(ChangeWithdrawalContext);

    if (!context) {
        throw new Error('useChangeWithdrawal must be used within a ChangeWithdrawalProvider');
    }

    const { state, dispatch, actions } = context;

    return { ...state, dispatch, actions };
}
